import { isString } from 'lodash';
import pluralize from 'pluralize';
import { memo, useCallback, useMemo, useState } from 'react';

import { type Library, WorkspaceMemberType, type WorkspaceUserRole } from '@air/api/types';
import { type SelectDropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';
import { convertUnknownToError } from '@air/utils-error';

import { useLibraryMembersAddMutation } from '~/components/LibraryBeta/hooks/mutations/useLibraryMembersAddMutation';
import { useLibrary } from '~/components/LibraryBeta/hooks/queries/useLibrary';
import { useLibraryMembers } from '~/components/LibraryBeta/hooks/useLibraryMembers';
import { useLibraryPermissions } from '~/components/LibraryBeta/hooks/useLibraryPermissions';
import { LibraryInviteMemberListItem } from '~/components/LibraryBeta/LibraryInviteMember/components/LibraryInviteMemberListItem';
import { LibraryInviteMemberRemovableChip } from '~/components/LibraryBeta/LibraryInviteMember/components/LibraryInviteMemberRemovableChip';
import { LibraryInviteToWorkspaceModal } from '~/components/LibraryBeta/LibraryInviteMember/components/LibraryInviteToWorkspaceModal';
import {
  LibraryInviteMember,
  type LibraryInviteMemberChipType,
  type LibraryInviteMemberProps,
} from '~/components/LibraryBeta/LibraryInviteMember/LibraryInviteMember';
import { type SelectProps } from '~/components/Select/Select';
import { useLibraryToasts } from '~/hooks/useLibraryToasts';
import { useWorkspaceMembers } from '~/swr-hooks/members/useWorkspaceMembers';
import { useWorkspaceRoles } from '~/swr-hooks/workspaces/useWorkspaceRoles';
import { isValidEmailAddress } from '~/utils/EmailUtils';
import { isPermissionsGTE } from '~/utils/PermissionsUtils';

export type LibraryInviteMemberContainerProps = {
  libraryId: Library['id'];
};

export const LibraryInviteMemberContainer = memo(({ libraryId }: LibraryInviteMemberContainerProps) => {
  const { data: roles } = useWorkspaceRoles();
  const { members } = useWorkspaceMembers();
  const { data: library } = useLibrary({ libraryId });
  const { data: libraryMembers } = useLibraryMembers({
    libraryId,
    options: {
      hideSysadmins: true,
      withPendingWorkspaceMembers: true,
    },
  });
  const { libraryMembersAddMutation } = useLibraryMembersAddMutation({ libraryId });
  const { showLibraryToast } = useLibraryToasts();
  const defaultRole = roles?.find((role) => role.id === library?.defaultRoleId);
  const [selectedMembers, setSelectedMembers] = useState<LibraryInviteMemberChipType[]>([]);
  const [selectedRole, setSelectRole] = useState<WorkspaceUserRole | undefined>(defaultRole);
  const [showWorkspaceInviteConfirm] = useAirModal(LibraryInviteToWorkspaceModal);
  const { libraryPermissions } = useLibraryPermissions({ libraryId });

  const libraryRoles: SelectDropdownMenuOption[] | undefined = useMemo(() => {
    if (!roles) {
      return undefined;
    }

    return roles
      .filter(({ type }) => type === WorkspaceMemberType.libraryMember)
      .filter((role) => role.enabled !== false)
      .map((role) => ({
        description: isString(role.description) ? role.description : undefined,
        disabled: !libraryPermissions || !isPermissionsGTE(libraryPermissions, role.permissions),
        id: role.id,
        label: role.displayName.replace('Library ', ''),
        onSelect: () => setSelectRole(role),
        type: 'item',
      }));
  }, [libraryPermissions, roles]);

  const nonWorkspaceMembers = useMemo(() => {
    return selectedMembers.filter((selectedMember) => {
      return !members?.activeMembers.find((member) => member.accountId === selectedMember.accountId);
    });
  }, [members, selectedMembers]);

  const helperText = useMemo(() => {
    if (!nonWorkspaceMembers.length) {
      return undefined;
    }

    return `${pluralize('user', nonWorkspaceMembers.length, true)} will also be invited to the
        workspace as an Editor`;
  }, [nonWorkspaceMembers]);

  const removeMember = useCallback((email: string) => {
    setSelectedMembers((prev) => prev.filter((member) => member.email !== email));
  }, []);

  const chipRenderer = useCallback(
    (member: LibraryInviteMemberChipType) => (
      <LibraryInviteMemberRemovableChip member={member} onRemove={removeMember} />
    ),
    [removeMember],
  );

  const listItemRenderer = useCallback((value: LibraryInviteMemberChipType) => {
    return (
      <LibraryInviteMemberListItem
        avatar={value.avatar ?? undefined}
        className="py-2"
        colorSeed={value.accountId ?? undefined}
        label={value.pending ? value.email : value.label}
        meta={value.pending ? 'Pending...' : value.email}
      />
    );
  }, []);

  const options = useMemo(() => {
    if (!members?.activeMembers || !libraryMembers?.length) {
      return [];
    }

    return members.activeMembers
      .filter((member) => !libraryMembers.find((libraryMember) => member.accountId === libraryMember.accountId))
      .map((member) => ({
        ...member,
        label: `${member.firstName} ${member.lastName}`,
        value: member.email,
      }));
  }, [members?.activeMembers, libraryMembers]);

  const onSelectionChange = useCallback<LibraryInviteMemberProps['onSelectionChange']>((values) => {
    setSelectedMembers([...values.chips]);
  }, []);

  const onNewMemberSelect = useCallback<Required<LibraryInviteMemberProps>['onCreate']>((value) => {
    setSelectedMembers((prev) => {
      return [
        ...prev,
        {
          email: value,
          value,
          label: value,
          avatar: '',
          accountId: '',
          pending: true,
          lastName: '',
          firstName: '',
        },
      ];
    });
  }, []);

  const inviteMembers = useCallback(async () => {
    libraryMembersAddMutation.mutate(
      selectedMembers.map((member) =>
        member.accountId
          ? {
              accountId: member.accountId,
              roleId: selectedRole?.id,
            }
          : {
              email: member.value,
              roleId: selectedRole?.id,
            },
      ),
      {
        onError: (error) => showLibraryToast({ message: convertUnknownToError(error).message, type: 'error' }),
        onSuccess: () => {
          showLibraryToast({
            message: (
              <>
                Invited{' '}
                <b className="ads:text-accent text-jay-200">{pluralize('user', selectedMembers.length, true)}</b> to
                library.
              </>
            ),
          });

          setSelectedMembers([]);
        },
      },
    );
  }, [libraryMembersAddMutation, selectedMembers, selectedRole?.id, showLibraryToast]);

  const allowValiedUniqEmail = useCallback<Required<SelectProps<LibraryInviteMemberChipType>>['createOptionFilter']>(
    (inputValue) => {
      /*
        We want to allow creating emails only if they are valid emails, are not yet invited, selected,
        and there is no user with that email
       */
      const isAlreadySelected = selectedMembers.find((member) => member.email === inputValue);
      const isAlreadyInvited = libraryMembers?.find((member) => member.email === inputValue);
      const isInOptions = options.find((option) => option.value === inputValue);

      return !isAlreadySelected && !isAlreadyInvited && !isInOptions && isValidEmailAddress(inputValue);
    },
    [libraryMembers, options, selectedMembers],
  );

  const handleInviteMember = useCallback(() => {
    if (nonWorkspaceMembers.length === 0) {
      void inviteMembers();
      return;
    }

    showWorkspaceInviteConfirm({
      nonWorkspaceMembers: nonWorkspaceMembers.map((member) => member.email),
      onConfirm: () => {
        void inviteMembers();
      },
    });
  }, [inviteMembers, nonWorkspaceMembers, showWorkspaceInviteConfirm]);

  return (
    <LibraryInviteMember
      chipRenderer={chipRenderer}
      listItemRenderer={listItemRenderer}
      onSelectionChange={onSelectionChange}
      options={options}
      buttonText={selectedMembers.length > 0 ? `Invite ${pluralize('user', selectedMembers.length, true)}` : 'Invite'}
      createOptionFilter={allowValiedUniqEmail}
      onInviteMember={handleInviteMember}
      helperText={helperText}
      isInviting={libraryMembersAddMutation.isPending}
      roles={libraryRoles}
      selectedRole={selectedRole}
      selectedOptions={selectedMembers}
      onCreate={onNewMemberSelect}
    />
  );
});

LibraryInviteMemberContainer.displayName = 'LibraryInviteMemberContainer';
